import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useSport } from "@/context/SportContext";
import { getSavedFilters, saveFilters, savedNearZip } from "@/lib/filterCookies";
import { useMyLoc, geoParams } from "@/lib/geoLocation";
import { groupIntoPrograms } from "@/lib/programs";
import { TeamCard, TrainerCard, EventCard } from "@/pages/Landing";

const TODAY_ISO = new Date().toISOString().slice(0, 10);

function Results({ q, sport, onNav, onViewList, onViewMap }) {
    const [data, setData] = useState({ teams: [], trainers: [], events: [], loading: true });
    const seq = useRef(0);
    const [loc] = useMyLoc();
    const zip = savedNearZip();

    useEffect(() => {
        const term = q.trim();
        if (!term) return undefined;
        const geo = geoParams(loc, zip);
        const id = ++seq.current;
        const fresh = () => seq.current === id;
        setData((d) => ({ ...d, loading: true }));
        const t = setTimeout(() => {
            const teamsP = api.get("/teams", { params: { search: term, sport, ...geo } }).then((r) => r.data).catch(() => []);
            const trReal = api.get("/trainers", { params: { search: term, sport, ...geo } }).then((r) => r.data).catch(() => []);
            const trList = api.get("/trainer-listings", { params: { sport, ...geo } }).then((r) => r.data).catch(() => []);
            const eventsP = api.get("/events", { params: { search: term, sport, when: "all", from_date: TODAY_ISO, ...geo } }).then((r) => r.data).catch(() => []);
            Promise.all([teamsP, trReal, trList, eventsP]).then(([teams, real, listings, events]) => {
                if (!fresh()) return;
                const ql = term.toLowerCase();
                const tagged = (listings || []).map((l) => ({ ...l, _listing: true }))
                    .filter((l) => `${l.name || ""} ${l.city || ""} ${l.state || ""}`.toLowerCase().includes(ql));
                setData({ teams: groupIntoPrograms(teams || []), trainers: [...(real || []), ...tagged], events: events || [], loading: false });
            });
        }, 200);
        return () => clearTimeout(t);
    }, [q, sport, loc, zip]);

    const rating = (x) => x.avg_rating ?? 0;
    const dateKey = (e) => e.event_date || e.end_date || "9999-12-31";
    const events = [...data.events].sort((a, b) => (dateKey(a) < dateKey(b) ? -1 : dateKey(a) > dateKey(b) ? 1 : 0));
    const teams = [...data.teams].sort((a, b) => rating(b) - rating(a));
    const trainers = [...data.trainers].sort((a, b) => rating(b) - rating(a));
    const total = events.length + teams.length + trainers.length;

    return (
        <div className="border-t border-[#2a2a2a] flex flex-col" style={{ maxHeight: "min(70vh, 520px)" }}>
            <div className="flex-1 overflow-y-auto min-h-0 p-2 space-y-2" data-testid="header-search-results">
                {data.loading ? (
                    <div className="py-12 flex items-center justify-center gap-2 text-sm text-[#a0a0a0]"><Loader2 className="h-4 w-4 animate-spin" /> Searching…</div>
                ) : total === 0 ? (
                    <div className="py-12 text-center text-sm text-[#a0a0a0]" data-testid="header-search-empty">No matches.</div>
                ) : (
                    <>
                        {events.map((e) => <EventCard key={`e-${e.id}`} e={e} testid={`header-search-row-event-${e.id}`} onNav={onNav} />)}
                        {teams.map((p) => <TeamCard key={`t-${p.key}`} p={p} testid={`header-search-row-team-${p.key}`} onNav={onNav} />)}
                        {trainers.map((tr) => <TrainerCard key={`r-${tr._listing ? "l" : ""}${tr.id}`} tr={tr} testid={`header-search-row-trainer-${tr.id}`} onNav={onNav} />)}
                    </>
                )}
            </div>
            <div className="shrink-0 border-t border-[#2a2a2a] bg-[#0c0c0c] grid grid-cols-2 gap-2 p-2">
                <button type="button" data-testid="header-search-view-map" onClick={onViewMap} className="h-11 inline-flex items-center justify-center gap-2 bg-[#ff4500] text-white text-xs uppercase tracking-[0.16em] font-semibold hover:bg-[#ff5722] transition-colors">
                    View on Map{total ? ` · ${total}` : ""}
                </button>
                <button type="button" data-testid="header-search-view-list" onClick={onViewList} className="h-11 inline-flex items-center justify-center gap-2 border border-[#2a2a2a] bg-[#1a1a1a] text-white text-xs uppercase tracking-[0.16em] font-semibold hover:border-[#ff4500] transition-colors">
                    View as List
                </button>
            </div>
        </div>
    );
}

// A header search icon that expands a full-width search bar with the same
// results as the main page search.
export default function HeaderSearch() {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const { sport } = useSport();
    const navigate = useNavigate();
    const inputRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const t = setTimeout(() => inputRef.current?.focus(), 60);
        const onKey = (e) => { if (e.key === "Escape") close(); };
        window.addEventListener("keydown", onKey);
        return () => { clearTimeout(t); window.removeEventListener("keydown", onKey); };
    }, [open]);

    const close = () => { setOpen(false); setQ(""); };

    const goResults = (view) => {
        const saved = getSavedFilters({});
        saveFilters({ ...saved, search: q.trim(), view });
        close();
        navigate("/");
    };

    return (
        <>
            <button
                type="button"
                data-testid="header-search-btn"
                onClick={() => setOpen(true)}
                title="Search"
                aria-label="Search"
                className="inline-flex items-center justify-center h-9 w-9 text-white/80 hover:text-[#ff4500] transition-colors"
            >
                <Search className="h-5 w-5" />
            </button>
            {open && (
                <div className="fixed inset-0 z-[1200]" data-testid="header-search-overlay">
                    <div className="absolute inset-0 bg-black/60" onClick={close} />
                    <div className="absolute top-3 left-0 right-0 px-4">
                        <div className="mx-auto max-w-3xl border border-[#2a2a2a] bg-[#141414] shadow-[0_24px_48px_rgba(0,0,0,0.7)]">
                            <div className="flex items-center">
                                <div className="px-4 flex items-center text-[#a0a0a0]"><Search className="h-4 w-4" /></div>
                                <input
                                    ref={inputRef}
                                    data-testid="header-search-input"
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") goResults("list"); }}
                                    placeholder="Search teams, trainers, and events…"
                                    className="h-14 flex-1 bg-transparent text-base text-white placeholder:text-[#666] outline-none"
                                />
                                <button type="button" data-testid="header-search-close" onClick={close} className="px-4 flex items-center text-[#a0a0a0] hover:text-[#ff4500] transition-colors">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                            {q.trim() && <Results q={q} sport={sport} onNav={close} onViewList={() => goResults("list")} onViewMap={() => goResults("map")} />}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
